export const SIMILARITY_APPROVAL_REQUIRED_MESSAGE =
  "Run similar question check before approval.";

export const normalizeAnswerImageId = (value) => {
  if (Array.isArray(value)) return typeof value[0] === "string" ? value[0] : "";
  if (typeof value === "string") return value;
  return "";
};

export const buildDefaultOptions = () => [
  {
    oID: "a1",
    answerExplanation: "",
    isCorrect: true,
    text: "",
    answerImageId: "",
  },
  {
    oID: "a2",
    answerExplanation: "",
    isCorrect: false,
    text: "",
    answerImageId: "",
  },
  {
    oID: "a3",
    answerExplanation: "",
    isCorrect: false,
    text: "",
    answerImageId: "",
  },
  {
    oID: "a4",
    answerExplanation: "",
    isCorrect: false,
    text: "",
    answerImageId: "",
  },
];

export const normalizeQuestionOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return buildDefaultOptions();
  }

  return options.map((opt, idx) => ({
    ...(opt || {}),
    oID: opt?.oID || `a${idx + 1}`,
    answerExplanation: opt?.answerExplanation || "",
    isCorrect: Boolean(opt?.isCorrect),
    text: opt?.text || "",
    answerImageId: normalizeAnswerImageId(
      opt?.answerImageId ?? opt?.answerImageIds ?? "",
    ),
  }));
};

export const createEmptyQuestionData = () => ({
  text: "",
  yield: "high",
  category: "",
  explanationRef: "",
  newQuestionImageIds: [],
  newQuestionImageCols: 1,
  newAnswerImageIds: [],
  newAnswerImageCols: 1,
  newAnswerImageCaptions: {},
  answerImageLocation: "bottom",
  questionSpecificExplanationHTML: "",
  aiQuestionImageDescription: "",
  aiAnswerImageDescription: "",
  options: buildDefaultOptions(),
});

const pendingExplanationApprovalIssues = new Set([
  "Finish editing the explanation before approval.",
  "Complete the new explanation before approval.",
  "Finish duplicating the explanation before approval.",
  "Resolve pending explanation changes before approval.",
  "Link an explanation to the question.",
]);

export const resolveQuestionApprovalIssuePanel = (issue) => {
  const normalizedIssue = typeof issue === "string" ? issue.trim() : "";

  if (
    normalizedIssue === "Question text is required." ||
    normalizedIssue === "Category must be selected."
  ) {
    return "basic";
  }

  if (normalizedIssue.startsWith("Complete all answer options")) {
    return "answers";
  }

  if (pendingExplanationApprovalIssues.has(normalizedIssue)) {
    return "clinical-knowledge";
  }

  if (normalizedIssue === SIMILARITY_APPROVAL_REQUIRED_MESSAGE) {
    return "similar";
  }

  return "approval";
};

export const stripHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const sanitizeComparableExplanationHtml = (html) => {
  if (typeof html !== "string" || html.trim() === "") return "";

  if (typeof DOMParser !== "undefined" && typeof Node !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    doc
      .querySelectorAll(
        "script,style,object,embed,form,input,button,select,textarea,meta,link",
      )
      .forEach((element) => element.remove());
    doc.body.querySelectorAll("*").forEach((element) => {
      Array.from(element.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim().toLowerCase();
        if (name.startsWith("on") || value.startsWith("javascript:")) {
          element.removeAttribute(attr.name);
        }
      });
    });
    return doc.body.innerHTML;
  }

  return html
    .replace(
      /<(script|style|object|embed|form|input|button|select|textarea|meta|link)\b[^>]*>[\s\S]*?<\/\1>/gi,
      "",
    )
    .replace(
      /<(script|style|object|embed|form|input|button|select|textarea|meta|link)\b[^>]*\/?>/gi,
      "",
    )
    .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\s+(href|src)\s*=\s*(["'])\s*javascript:[\s\S]*?\2/gi, "");
};

export const simpleDiff = (oldText, newText) => {
  oldText = oldText || "";
  newText = newText || "";

  if (oldText === newText) {
    return {
      oldContent: oldText,
      newContent: newText,
      diffHtml: `<div class="no-changes">No changes detected</div>`,
    };
  }

  const oldTokens = oldText.split(/(\s+)/).filter((token) => token.length > 0);
  const newTokens = newText.split(/(\s+)/).filter((token) => token.length > 0);
  const dp = Array.from({ length: oldTokens.length + 1 }, () =>
    Array(newTokens.length + 1).fill(0),
  );

  for (let i = 1; i <= oldTokens.length; i += 1) {
    for (let j = 1; j <= newTokens.length; j += 1) {
      dp[i][j] =
        oldTokens[i - 1] === newTokens[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  let i = oldTokens.length;
  let j = newTokens.length;
  const diffTokens = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      diffTokens.unshift(oldTokens[i - 1]);
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      diffTokens.unshift(
        `<span style="background-color: #ccffcc; font-weight: bold;">${
          newTokens[j - 1]
        }</span>`,
      );
      j -= 1;
    } else {
      diffTokens.unshift(
        `<span style="background-color: #ffcccc; text-decoration: line-through;">${
          oldTokens[i - 1]
        }</span>`,
      );
      i -= 1;
    }
  }

  return {
    oldContent: oldText,
    newContent: newText,
    diffHtml: `<div class="diff">${diffTokens.join("")}</div>`,
  };
};

export const buildComparableQuestion = (
  data,
  { sanitizeExplanation = sanitizeComparableExplanationHtml } = {},
) => {
  if (!data || typeof data !== "object") return null;

  const normalizeStringArray = (value) =>
    Array.isArray(value) ? value.map((item) => item) : [];

  const options = Array.isArray(data.options)
    ? data.options.map((opt, idx) => ({
        text: opt?.text || "",
        answerExplanation: opt?.answerExplanation || "",
        isCorrect: Boolean(opt?.isCorrect),
        answerImageId: normalizeAnswerImageId(
          opt?.answerImageId ?? opt?.answerImageIds ?? "",
        ),
        oID: opt?.oID || `a${idx + 1}`,
      }))
    : [];

  return {
    text: data.text || "",
    categoryId: data.category?.id || "",
    questionSpecificExplanationHTML: sanitizeExplanation(
      data.questionSpecificExplanationHTML || "",
    ),
    newQuestionImageIds: normalizeStringArray(data.newQuestionImageIds),
    newAnswerImageIds: normalizeStringArray(data.newAnswerImageIds),
    newAnswerImageCaptions: data.newAnswerImageCaptions || {},
    answerImageLocation: data.answerImageLocation || "",
    explanationRefId: data.explanationRef?.id || "",
    options,
  };
};

const arrayIdsChanged = (left = [], right = []) => {
  if (left.length !== right.length) return true;
  return right.some((id) => !left.includes(id));
};

const formatCaptionMap = (map) =>
  Object.entries(map || {})
    .map(([id, cap]) => `${id}: ${cap}`)
    .join("<br/>") || "None";

export const buildQuestionChanges = (
  questionData,
  originalQuestionData,
  { explanationTitle = "" } = {},
) => {
  if (!originalQuestionData) return [];

  const changes = [];

  if (questionData.category?.id !== originalQuestionData.category?.id) {
    changes.push({
      field: "Category",
      old: originalQuestionData.category?.id || "None",
      new: questionData.category?.id || "None",
    });
  }

  if (questionData.text !== originalQuestionData.text) {
    const diff = simpleDiff(originalQuestionData.text, questionData.text);
    changes.push({
      field: "Question Text",
      diffHtml: diff.diffHtml,
      old: originalQuestionData.text || "None",
      new: questionData.text || "None",
    });
  }

  if (
    arrayIdsChanged(
      originalQuestionData.newQuestionImageIds || [],
      questionData.newQuestionImageIds || [],
    )
  ) {
    changes.push({
      field: "Question Image",
      old: originalQuestionData.newQuestionImageIds || "None",
      new: questionData.newQuestionImageIds || "None",
      image: true,
    });
  }

  (questionData.options || []).forEach((opt, idx) => {
    const origOpt = originalQuestionData.options?.[idx];
    if (!origOpt) return;

    if (opt.text !== origOpt.text) {
      const diff = simpleDiff(origOpt.text, opt.text);
      changes.push({
        field: `Option ${idx + 1} Text`,
        diffHtml: diff.diffHtml,
        old: origOpt.text || "None",
        new: opt.text || "None",
      });
    }

    if (opt.answerExplanation !== origOpt.answerExplanation) {
      const diff = simpleDiff(origOpt.answerExplanation, opt.answerExplanation);
      changes.push({
        field: `Option ${idx + 1} Explanation`,
        diffHtml: diff.diffHtml,
        old: origOpt.answerExplanation || "None",
        new: opt.answerExplanation || "None",
      });
    }

    const originalOptionImageId = normalizeAnswerImageId(
      origOpt.answerImageId ?? origOpt.answerImageIds ?? "",
    );
    const updatedOptionImageId = normalizeAnswerImageId(
      opt.answerImageId ?? opt.answerImageIds ?? "",
    );
    if (originalOptionImageId !== updatedOptionImageId) {
      changes.push({
        field: `Option ${idx + 1} Image`,
        old: originalOptionImageId ? [originalOptionImageId] : "None",
        new: updatedOptionImageId ? [updatedOptionImageId] : "None",
        image: true,
      });
    }
  });

  const originalExplanationText = stripHtml(
    originalQuestionData.questionSpecificExplanationHTML || "",
  );
  const newExplanationText = stripHtml(
    questionData.questionSpecificExplanationHTML || "",
  );
  if (originalExplanationText !== newExplanationText) {
    const diff = simpleDiff(originalExplanationText, newExplanationText);
    changes.push({
      field: "Question Specific Explanation",
      diffHtml: diff.diffHtml,
      old: originalQuestionData.questionSpecificExplanationHTML || "None",
      new: questionData.questionSpecificExplanationHTML || "None",
    });
  }

  if (
    arrayIdsChanged(
      originalQuestionData.newAnswerImageIds || [],
      questionData.newAnswerImageIds || [],
    )
  ) {
    changes.push({
      field: "Answer Image",
      old: originalQuestionData.newAnswerImageIds || "None",
      new: questionData.newAnswerImageIds || "None",
      image: true,
    });
  }

  if (
    JSON.stringify(questionData.newAnswerImageCaptions || {}) !==
    JSON.stringify(originalQuestionData.newAnswerImageCaptions || {})
  ) {
    const oldCaps = formatCaptionMap(
      originalQuestionData.newAnswerImageCaptions,
    );
    const newCaps = formatCaptionMap(questionData.newAnswerImageCaptions);
    const diff = simpleDiff(oldCaps, newCaps);
    changes.push({
      field: "Answer Image Captions",
      diffHtml: diff.diffHtml,
      old: oldCaps,
      new: newCaps,
    });
  }

  if (
    questionData.answerImageLocation !==
    originalQuestionData.answerImageLocation
  ) {
    changes.push({
      field: "Answer Image Placement",
      old: originalQuestionData.answerImageLocation || "Default",
      new: questionData.answerImageLocation || "Default",
    });
  }

  if (
    questionData.explanationRef?.id !== originalQuestionData.explanationRef?.id
  ) {
    changes.push({
      field: "Clinical Knowledge Explanation",
      old: originalQuestionData.explanationTitle || "None",
      new: explanationTitle || "None",
    });
  }

  return changes;
};

export const buildQuestionSectionStatuses = ({
  questionData,
  validation,
  similarSearched = false,
  reviewContentPresent = false,
}) => {
  const questionImageCount = Array.isArray(questionData?.newQuestionImageIds)
    ? questionData.newQuestionImageIds.length
    : 0;
  const answerImageCount = Array.isArray(questionData?.newAnswerImageIds)
    ? questionData.newAnswerImageIds.length
    : 0;
  const requiresSimilarityApproval = Boolean(
    validation?.requiresSimilarityApproval,
  );
  const similarityApprovalReady = requiresSimilarityApproval
    ? Boolean(validation?.similarityApprovalReady)
    : true;

  return {
    basic: {
      complete:
        Boolean(validation?.questionTextPresent) &&
        Boolean(validation?.categorySelected),
    },
    "question-images": {
      complete: questionImageCount > 0,
      optional: true,
    },
    answers: {
      complete: Boolean(validation?.allOptionsHaveContent),
    },
    similar: {
      complete: requiresSimilarityApproval
        ? similarityApprovalReady
        : Boolean(similarSearched),
      optional: !requiresSimilarityApproval,
    },
    "specific-explanation": {
      complete:
        stripHtml(questionData?.questionSpecificExplanationHTML || "").length >
          0 || answerImageCount > 0,
      optional: true,
    },
    "clinical-knowledge": {
      complete: Boolean(validation?.explanationLinked),
    },
    review: {
      complete: Boolean(reviewContentPresent),
      optional: true,
    },
    approval: {
      complete: Boolean(validation?.isQuestionComplete),
    },
  };
};
